/** @domain convertible -- Convertible instrument fields: instrument number/type, principal, discount, interest, maturity. */
import { z } from 'zod';
import { createDomainField } from './types';
import { insertCommas, limitDecimalPlaces, trimExtraSpaces } from './formatters';

const define = createDomainField('convertible');

const currencyFormatter: Array<(v: string) => string> = [
  (v) => v.replace(/[^0-9.]/g, ''),
  limitDecimalPlaces(2),
  insertCommas,
];

export const instrumentNumber = define({
  label: 'Instrument Number',
  placeholder: 'Enter instrument number',
  schema: z.string().max(100, 'Must be at most 100 characters'),
  adapterHint: 'text',
  formatter: trimExtraSpaces,
  displayFormat: 'text',
});

export const instrumentType = define({
  label: 'Instrument Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const securityType = define({
  label: 'Security Type',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const principalAmount = define({
  label: 'Principal Amount ($)',
  placeholder: 'Enter principal amount',
  schema: z.string(),
  adapterHint: 'text',
  formatter: currencyFormatter,
  displayFormat: 'currency',
});

export const issueDate = define({
  label: 'Issue Date',
  schema: z.string(),
  adapterHint: 'date',
  displayFormat: 'date',
});

export const valuationCap = define({
  label: 'Valuation Cap ($)',
  placeholder: 'Enter valuation cap',
  schema: z.string(),
  adapterHint: 'text',
  formatter: currencyFormatter,
  displayFormat: 'currency',
});

export const valuationCapMode = define({
  label: 'Valuation Cap Basis',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const conversionDiscount = define({
  label: 'Conversion Discount (%)',
  placeholder: 'Enter conversion discount',
  schema: z.string().regex(/^(\d+(\.\d{0,2})?)$/, 'Must be a valid percentage'),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'percentage',
});

export const interestRate = define({
  label: 'Interest Rate (%)',
  placeholder: 'Enter interest rate',
  schema: z.string(),
  adapterHint: 'number',
  formatter: limitDecimalPlaces(2),
  displayFormat: 'percentage',
});

export const maturityDate = define({
  label: 'Maturity Date',
  schema: z.string(),
  adapterHint: 'date',
  displayFormat: 'date',
});

export const qualifiedFinancingThreshold = define({
  label: 'Qualified Financing Threshold ($)',
  placeholder: 'Enter threshold amount',
  schema: z.string(),
  adapterHint: 'text',
  formatter: currencyFormatter,
  displayFormat: 'currency',
});

export const isQualifiedFinancingRound = define({
  label: 'Qualified Financing Round',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const requiresAcceptance = define({
  label: 'Requires Acceptance',
  schema: z.string(),
  adapterHint: 'select',
  displayFormat: 'text',
});

export const details = define({
  label: 'Details',
  placeholder: 'Enter details',
  schema: z.string().max(5000, 'Must be at most 5000 characters'),
  adapterHint: 'textarea',
  displayFormat: 'text',
});
